import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import {
  EuiButton, EuiButtonEmpty, EuiCallOut, EuiFieldText, EuiFormRow,
  EuiLoadingSpinner, EuiModal, EuiModalBody, EuiModalFooter,
  EuiModalHeader, EuiModalHeaderTitle, EuiOverlayMask, EuiSelect,
  EuiSpacer, EuiText, EuiTextArea,
} from '@elastic/eui';
import { TranslateLanguage } from './KeywordTranslator';

const FIELD_LABELS = {
  title: '標題', description: '內容／說明', verse: '經文', prayer: '禱告內容',
  source: '來源內容', publisher: '發佈者', link: '來源連結', category: '分類',
  speaker: '講員', language: '語言', tempo: '速度', official: '官方標記',
  verselist: '相關經文', origin: '來源', length: '時長', tag: '標籤',
  date: '日期', viewct: '觀看次數', testment: '書別',
  chapter_name: '書卷', chapter_code_name: '書卷代碼', chapter_number: '章',
  chapter_verse_number: '章節', verse_number: '節', book_number: '書卷編號',
  verse_id: '經文編號', lastupdate: '最後更新',
};
const LONG_FIELDS = new Set(['description', 'verse', 'prayer', 'source', 'origin']);
const displayValue = value => Array.isArray(value) ? value.join('\n') : String(value ?? '');
const editableFields = document => Object.keys(document || {})
  .filter(field => !['id', 'doctype'].includes(field) && !field.startsWith('_'))
  .filter(field => {
    const value = document[field];
    return typeof value === 'string' || typeof value === 'number' ||
      typeof value === 'boolean' || (Array.isArray(value) && value.every(item => typeof item === 'string'));
  })
  .sort((a, b) => {
    const order = ['title', 'verse', 'description', 'prayer', 'source', 'link'];
    const aIndex = order.indexOf(a);
    const bIndex = order.indexOf(b);
    return (aIndex < 0 ? order.length : aIndex) - (bIndex < 0 ? order.length : bIndex) || a.localeCompare(b);
  });

const parseValue = (text, original) => {
  if (Array.isArray(original)) return text.split(/\r?\n/).map(item => item.trim()).filter(Boolean);
  if (typeof original === 'number') {
    if (!text.trim() || !Number.isFinite(Number(text))) throw new Error('數字欄位必須填寫有效數字。');
    return Number(text);
  }
  if (typeof original === 'boolean') return text === 'true';
  return text;
};

const isYouTubeVideoUrl = value => {
  try {
    const url = new URL(value);
    if (!['http:', 'https:'].includes(url.protocol)) return false;
    if (url.hostname === 'youtu.be') return url.pathname.length > 1;
    if (!['youtube.com', 'www.youtube.com', 'm.youtube.com', 'music.youtube.com',
      'youtube-nocookie.com', 'www.youtube-nocookie.com'].includes(url.hostname)) return false;
    return (url.pathname === '/watch' && Boolean(url.searchParams.get('v'))) ||
      /^\/(shorts|live|embed|v)\/[^/]+/i.test(url.pathname);
  } catch {
    return false;
  }
};

export default function SearchResultEditor({ scopedId, title, token, language, onClose, onDeleted }) {
  const [document, setDocument] = useState(null);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleted, setDeleted] = useState(false);
  const [message, setMessage] = useState(null);
  const fields = useMemo(() => editableFields(document), [document]);
  const headers = {
    Authorization: `Bearer ${token}`,
    ...(process.env.REACT_APP_PostKey ? { 'X-Elastic-Authorization': process.env.REACT_APP_PostKey } : {}),
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    axios.get('/api/admin/search-documents/result', {
      params: { id: scopedId }, headers,
    }).then(response => {
      if (!active) return;
      const loaded = response.data.fields;
      setDocument(loaded);
      setDraft(Object.fromEntries(editableFields(loaded).map(field => [field, displayValue(loaded[field])])));
    }).catch(error => {
      if (active) setMessage({ color: 'danger', text: error.response?.data?.message || '無法讀取原始文件。' });
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [scopedId]);

  const save = async () => {
    if (deleted || deleting) return;
    const changes = {};
    try {
      fields.forEach(field => {
        if (draft[field] !== displayValue(document[field]))
          changes[field] = parseValue(draft[field], document[field]);
      });
      if (!Object.keys(changes).length) {
        setMessage({ color: 'warning', text: '沒有需要儲存的修改。' });
        return;
      }
    } catch (error) {
      setMessage({ color: 'danger', text: error.message });
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      await axios.patch('/api/admin/search-documents/result', { id: scopedId, fields: changes }, { headers });
      setDocument(current => ({ ...current, ...changes }));
      setMessage({ color: 'success', text: '已儲存到 Elastic。搜尋索引可能需要片刻更新；重新搜尋即可看到新內容。' });
    } catch (error) {
      const response = error.response?.data;
      const details = Array.isArray(response?.details) ? response.details.join('；') : '';
      setMessage({ color: 'danger', text: [response?.message || '儲存失敗，請稍後再試。', details].filter(Boolean).join(' ') });
    } finally {
      setSaving(false);
    }
  };

  const deleteVideo = async () => {
    if (!document || !isYouTubeVideoUrl(document.link) || saving || deleting || deleted) return;
    if (!window.confirm(TranslateLanguage(language,
      `確定永久刪除「${title}」？這會從 Elastic 刪除這條搜尋結果，無法在此復原。`))) return;

    setDeleting(true);
    setMessage(null);
    try {
      await axios.delete('/api/admin/search-documents/result', { params: { id: scopedId }, headers });
      setDeleted(true);
      setMessage({ color: 'success', text: '已從 Elastic 刪除。搜尋索引可能需要片刻更新。' });
      onDeleted?.(scopedId);
    } catch (error) {
      const response = error.response?.data;
      setMessage({ color: 'danger', text: response?.message || '刪除失敗，請稍後再試。' });
    } finally {
      setDeleting(false);
    }
  };

  return <EuiOverlayMask>
    <EuiModal
      className={Capacitor.isNativePlatform() ? 'search-result-editor-modal-native' : undefined}
      onClose={() => { if (!deleting) onClose(); }}
      style={{ width: 'min(760px, 96vw)', maxWidth: 760 }}
    >
      <EuiModalHeader><EuiModalHeaderTitle>{TranslateLanguage(language, '編輯搜尋內容')}</EuiModalHeaderTitle></EuiModalHeader>
      <EuiModalBody>
        <EuiText size="s"><strong>{title}</strong><br />{scopedId}</EuiText>
        <EuiSpacer size="m" />
        {loading && <EuiLoadingSpinner size="xl" />}
        {!loading && document && !deleted && fields.map(field => {
          const value = document[field];
          const label = TranslateLanguage(language, FIELD_LABELS[field] || field);
          return <EuiFormRow key={field} label={label} helpText={field} fullWidth>
            {typeof value === 'boolean' ?
              <EuiSelect fullWidth value={draft[field]} onChange={event => setDraft(current => ({ ...current, [field]: event.target.value }))}
                options={[{ value: 'true', text: 'true' }, { value: 'false', text: 'false' }]} /> :
              (Array.isArray(value) || LONG_FIELDS.has(field) || String(value).length > 120) ?
                <EuiTextArea fullWidth rows={Array.isArray(value) ? 3 : 5} value={draft[field] ?? ''}
                  onChange={event => setDraft(current => ({ ...current, [field]: event.target.value }))} /> :
                <EuiFieldText fullWidth value={draft[field] ?? ''}
                  onChange={event => setDraft(current => ({ ...current, [field]: event.target.value }))} />}
          </EuiFormRow>;
        })}
      </EuiModalBody>
      <EuiModalFooter>
        <div style={{ width: '100%' }}>
          {message && <div role="status" aria-live="polite" style={{ marginBottom: 12 }}>
            <EuiCallOut color={message.color} title={TranslateLanguage(language, message.text)} />
          </div>}
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {!deleted && isYouTubeVideoUrl(document?.link) &&
              <EuiButtonEmpty color="danger" iconType="trash" onClick={deleteVideo}
                isLoading={deleting} isDisabled={saving} style={{ marginRight: 'auto' }}>
                {TranslateLanguage(language, '刪除影片')}
              </EuiButtonEmpty>}
            <EuiButtonEmpty onClick={onClose} isDisabled={deleting}>{TranslateLanguage(language, '關閉')}</EuiButtonEmpty>
            {!deleted && <EuiButton fill onClick={save} isLoading={saving}
              isDisabled={loading || !document || deleting}>
              {TranslateLanguage(language, '儲存修改')}
            </EuiButton>}
          </div>
        </div>
      </EuiModalFooter>
    </EuiModal>
  </EuiOverlayMask>;
}
